import {
  Package, AlertTriangle, Search, Filter 
} from 'lucide-react';
import {
  Card, CardContent, CardHeader, CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CrudToolbar } from '@/components/ui/crud-toolbar';
import {
  FormModal, FormField, FormInput, FormSelect 
} from '@/components/ui/form-modal';
import { StockLevelCard } from '@/features/inventory/components';
import type { StockItem } from '@/features/inventory/types';
import {
  ADJUSTMENT_TYPE_OPTIONS, ADJUSTMENT_REASON_OPTIONS 
} from './inventory-constants';

interface AdjustFormData {
  itemId: string;
  adjustmentType: 'add' | 'remove' | 'set';
  quantity: string;
  reason: string;
}

interface StockViewProps {
  readonly items: StockItem[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly totalItems: number;
  readonly lowStockItems: number;
  readonly outOfStockItems: number;
  readonly lowStockOnly: boolean;
  readonly setLowStockOnly: (value: boolean) => void;
  readonly selectedItems: string[];
  readonly toggleItemSelection: (itemId: string) => void;
  readonly showAdjustModal: boolean;
  readonly setShowAdjustModal: (show: boolean) => void;
  readonly adjustFormData: AdjustFormData;
  readonly setAdjustFormData: (data: AdjustFormData) => void;
  readonly onAdjustStock: () => void;
  readonly onSaveAdjustment: () => void;
  readonly onBulkUpload: (file: File) => void;
  readonly onRefetch: () => void;
  readonly exportData: Record<string, unknown>[];
}

export function StockView({
  items,
  isLoading,
  error,
  totalItems,
  lowStockItems,
  outOfStockItems,
  lowStockOnly,
  setLowStockOnly,
  selectedItems,
  toggleItemSelection,
  showAdjustModal,
  setShowAdjustModal,
  adjustFormData,
  setAdjustFormData,
  onAdjustStock,
  onSaveAdjustment,
  onBulkUpload,
  onRefetch,
  exportData,
}: StockViewProps) {
  return (
    <>
      <CrudToolbar
        entityName="Stock Level"
        entityNamePlural="Stock Levels"
        selectedCount={selectedItems.length}
        onEdit={onAdjustStock}
        onBulkUpload={onBulkUpload}
        exportData={exportData}
        exportFilename="inventory-stock.csv"
        canAdd={false}
        canDelete={false}
      />

      <FormModal
        title="Adjust Stock"
        description="Adjust stock level for selected item"
        isOpen={showAdjustModal}
        onClose={() => setShowAdjustModal(false)}
        onSave={onSaveAdjustment}
        saveLabel="Apply Adjustment"
      >
        <div className="grid gap-4 md:grid-cols-2">
          <FormField label="Adjustment Type" required>
            <FormSelect
              value={adjustFormData.adjustmentType}
              onChange={(e) => setAdjustFormData({
                ...adjustFormData,
                adjustmentType: e.target.value as 'add' | 'remove' | 'set' 
              })}
              options={ADJUSTMENT_TYPE_OPTIONS}
            />
          </FormField>
          <FormField label="Quantity" required>
            <FormInput
              type="number"
              min="0"
              value={adjustFormData.quantity}
              onChange={(e) => setAdjustFormData({
                ...adjustFormData,
                quantity: e.target.value 
              })}
              placeholder="Enter quantity"
            />
          </FormField>
        </div>
        <FormField label="Reason" required>
          <FormSelect
            value={adjustFormData.reason}
            onChange={(e) => setAdjustFormData({
              ...adjustFormData,
              reason: e.target.value 
            })}
            placeholder="Select reason..."
            options={ADJUSTMENT_REASON_OPTIONS}
          />
        </FormField>
      </FormModal>

      <SummaryCards totalItems={totalItems} lowStockItems={lowStockItems} outOfStockItems={outOfStockItems} />

      <div className="flex items-center gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search items..."
            className="w-full rounded-md border bg-background py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
        <Button variant={lowStockOnly ? 'default' : 'outline'} size="sm" onClick={() => setLowStockOnly(!lowStockOnly)}>
          <Filter className="mr-2 h-4 w-4" />
          {lowStockOnly ? 'Showing Low Stock' : 'Show Low Stock Only'}
        </Button>
      </div>

      <StockGrid
        items={items}
        isLoading={isLoading}
        error={error}
        selectedItems={selectedItems}
        toggleItemSelection={toggleItemSelection}
        onRefetch={onRefetch}
      />
    </>
  );
}

function SummaryCards({
  totalItems,
  lowStockItems,
  outOfStockItems,
}: {
  readonly totalItems: number;
  readonly lowStockItems: number;
  readonly outOfStockItems: number;
}) {
  return (
    <div className="grid gap-4 md:grid-cols-3">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Total Items</CardTitle>
          <Package className="h-5 w-5 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{totalItems}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Low Stock</CardTitle>
          <AlertTriangle className="h-5 w-5 text-amber-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-amber-600">{lowStockItems}</div>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">Out of Stock</CardTitle>
          <AlertTriangle className="h-5 w-5 text-red-500" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-red-600">{outOfStockItems}</div>
        </CardContent>
      </Card>
    </div>
  );
}

function StockGrid({
  items,
  isLoading,
  error,
  selectedItems,
  toggleItemSelection,
  onRefetch,
}: {
  readonly items: StockItem[];
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly selectedItems: string[];
  readonly toggleItemSelection: (itemId: string) => void;
  readonly onRefetch: () => void;
}) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={`skeleton-${i.toString()}`} className="h-48 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <AlertTriangle className="mb-4 h-12 w-12 text-red-500" />
          <p className="text-lg font-medium">Failed to load inventory</p>
          <Button variant="outline" className="mt-4" onClick={onRefetch}>Try Again</Button>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Package className="mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-lg font-medium">No inventory items found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <div
          key={item.itemId}
          className={`relative cursor-pointer rounded-lg transition-all ${
            selectedItems.includes(item.itemId) ? 'ring-2 ring-primary' : ''
          }`}
          onClick={() => toggleItemSelection(item.itemId)}
        >
          {selectedItems.includes(item.itemId) && (
            <div className="absolute top-2 right-2 z-10 h-5 w-5 rounded-full bg-primary flex items-center justify-center">
              <span className="text-xs text-primary-foreground">✓</span>
            </div>
          )}
          <StockLevelCard item={item} />
        </div>
      ))}
    </div>
  );
}
